import "./App.css";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useState, useRef } from "react";
import { Nav, Navbar, Offcanvas } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import L, { LatLng, Icon, Map } from "leaflet";
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { layer, library } from '@fortawesome/fontawesome-svg-core';
import { Logout, getAllDevicesDate, DateFormatWithSlash, calculateVoltage, moveTo, addUsageRecord, getLastestDataBatch } from './ELTRES_cmnFunc';
import { Data, DeviceData, UsageRecord } from "./ELTRES_cmnDefine";


//--------------Data from DB-------------------
type Pins = { position: LatLng, name: string, devId: number, dateTime: string, adc: string, rssi: number, visible: boolean, color: string, initialColor: string }[];
const initialPins: { position: LatLng, name: string, devId: number, dateTime: string, adc: string, rssi: number, visible: boolean, color: string, initialColor: string }[] = [];

function createIcon(color: string) {
  return new Icon({
    iconUrl: require(`./img/${color}.png`),
    iconSize: [30, 30],
    iconAnchor: [12, 41],
    popupAnchor: [0, -41],
  });
}

library.add(faMapMarkerAlt);

export function Tracking(): JSX.Element {
  const uId = sessionStorage.getItem('u_id')?.toString();
  const authority = sessionStorage.getItem('authority')?.toString();
  const cIDSess = sessionStorage.getItem('client_id')?.toString();
  const client_id = cIDSess && !isNaN(parseInt(cIDSess)) ? parseInt(cIDSess) : null;
  const u_id = uId ? JSON.parse(uId).u_id : 0;
  const savedLayer = localStorage.getItem('selectedLayer');
  const mapRef = React.createRef<HTMLDivElement>();
  const [map, setMap] = useState<Map | null>(null);
  const [layer, setLayer] = useState(savedLayer || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [allDevicesData, setAllDevicesData] = useState<DeviceData[]>([]);
  const [selectedDevIds, setSelectedDevIds] = useState<number[]>([]);
  const [pins, setPins] = useState(initialPins);
  const allColors: string[] = ['red', 'blue', 'green', 'orange', 'purple', 'pink', 'blueViolet', 'yellow', 'lightBlue', 'lightGreen'];
  const [initialDevIdToColorMap, setInitialDevIdToColorMap] = useState<Record<number, string>>({});
  const [currentColorMap, setCurrentColorMap] = useState<Record<number, string>>({});
  const [deviceDataAvailability, setDeviceDataAvailability] = useState<Record<number, boolean>>({});
  const showAdminLinks = authority !== '2';
  const isClientAdmin = authority == '1';
  const [markerRefs, setMarkerRefs] = useState<globalThis.Map<number, L.Marker[]>>(new globalThis.Map());
  const isFirstRender = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const fetchDoneRef = useRef(false);
  const hasAppliedNavState = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  function calculateVisibleTiles(map: L.Map, client_id: number | null, uId: number) {
    const bounds = map.getBounds();
    const tileSize = 256;
    const nw = bounds.getNorthWest();
    const se = bounds.getSouthEast();

    const nwTile = map.project(nw, map.getZoom()).divideBy(tileSize).floor();
    const seTile = map.project(se, map.getZoom()).divideBy(tileSize).floor();

    const visibleTilesCount = (seTile.x - nwTile.x + 1) * (seTile.y - nwTile.y + 1);
    // console.log('Visible Tiles Count:', visibleTilesCount);
    localStorage.setItem('visibleTilesCount', JSON.stringify(visibleTilesCount));

    const usage: UsageRecord = {
      use_date: new Date().toISOString(),
      client_id: client_id,
      u_id: uId,
      event_type: 0,
      tile_count: visibleTilesCount
    };
    addUsageRecord(usage);
  };

  function buildLatestDataResult(
    devices: DeviceData[],
    latestDataResults: Data[]
  ) {
    const dataAvailability: Record<number, boolean> = {};

    const latestDataMap = new globalThis.Map<number, Data>();
    latestDataResults.forEach((data) => {
      latestDataMap.set(data.dev_id, data);
    });

    devices.forEach((device) => {
      const data = latestDataMap.get(device.dev_id);

      if (
        data !== undefined &&
        data.latitude !== null &&
        data.longitude !== null &&
        data.latitude <= 90 && data.latitude >= -90 &&
        data.longitude <= 180 && data.longitude >= -180
      ) {
        dataAvailability[device.dev_id] = true;
      } else {
        dataAvailability[device.dev_id] = false;
      }
    });

    const validLatestData = devices
      .map((device) => latestDataMap.get(device.dev_id) ?? null)
      .filter((data): data is Data =>
        data !== null &&
        data.latitude !== null &&
        data.longitude !== null &&
        data.latitude <= 90 && data.latitude >= -90 &&
        data.longitude <= 180 && data.longitude >= -180
      );

    const newPins = validLatestData.map((data) => ({
      position: new LatLng(
        parseFloat(data.latitude!.toFixed(6)),
        parseFloat(data.longitude!.toFixed(6))
      ),
      name: devices.find((device) => device.dev_id === data.dev_id)?.dev_name || "Unknown",
      devId: data.dev_id,
      dateTime: DateFormatWithSlash(new Date(data.tx_time)),
      adc: data.src_02 != null? calculateVoltage(data.src_02) : "",
      rssi: data.rssi ?? 0,
      visible: true,
      initialColor: "",
      color: "",
    }));

    return { dataAvailability, newPins };
  }

  const fetchData = async () => {
    setIsLoading(true);
    fetchDoneRef.current = false;

    try {
      let devices = await getAllDevicesDate();
      if (!devices) devices = [];

      devices = devices.filter((d: any) => d.enable === true);
      if (isClientAdmin && cIDSess) {
        devices = devices.filter((d: any) => d.client_id == parseInt(cIDSess));
      }

      const devIds = devices.map((d: DeviceData) => d.dev_id);
      const latestDataResults = await getLastestDataBatch(devIds);
      
      const { dataAvailability, newPins } = buildLatestDataResult(devices, latestDataResults);
      
      const initialDevIds: number[] = devices.slice(0, 10).map((d: DeviceData) => d.dev_id);
      setSelectedDevIds(initialDevIds);
      setDeviceDataAvailability(dataAvailability);
      setAllDevicesData(devices);
      
      const initMap: Record<number, string> = {};
      devices.forEach((d: DeviceData, i: number) => {
        initMap[d.dev_id] = allColors[i % allColors.length];
      });
    
      setInitialDevIdToColorMap(initMap);
    
      const used = new Set<string>();
      const nextColorMap: Record<number, string> = {};
      initialDevIds.forEach(id => {
        const preferred = initMap[id];
        let use = preferred;
        if (used.has(use)) {
          const free = allColors.find(c => !used.has(c));
          if (free) use = free;
        }
        used.add(use);
        nextColorMap[id] = use;
      });
    
      setCurrentColorMap(nextColorMap);
      setPins(newPins);


    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    } finally {
      fetchDoneRef.current = true;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      const visibleTilesCount = localStorage.getItem('visibleTilesCount');
      const usage: UsageRecord = {
        use_date: new Date().toISOString(),
        client_id: client_id,
        u_id: u_id,
        event_type: 0,
        tile_count: visibleTilesCount ? parseInt(visibleTilesCount) : 0
      };
      addUsageRecord(usage);
      isFirstRender.current = false;
      // return;
    }

    if (!fetchDoneRef.current) return;

    let selectedFromNav: number[] | null = null;
    let colorMapFromNav: Record<number, string> | null = null;
    
    if (!hasAppliedNavState.current) {   // only use location.state once
      const navState = (location.state as { selected?: number[]; colorMap?: Record<number,string> } | null) ?? null;
      if (navState?.selected && navState?.colorMap) {
        const valid = navState.selected
          .filter(id => allDevicesData.some(d => d.dev_id === id))
          .slice(0, 10);
      
        if (valid.length) {
          selectedFromNav = valid;
        
          const used = new Set<string>();
          const nextMap: Record<number, string> = {};
          valid.forEach(id => {
            const preferred = navState.colorMap![id] ?? initialDevIdToColorMap[id];
            let use = preferred;
            if (used.has(use)) {
              const free = allColors.find(c => !used.has(c));
              if (free) use = free;
            }
            used.add(use);
            nextMap[id] = use;
          });
          colorMapFromNav = nextMap;
        
          setSelectedDevIds(valid);
          setCurrentColorMap(nextMap);
        
          navigate(location.pathname, { replace: true, state: null });
          hasAppliedNavState.current = true;
        }
      }
    }

    const effectiveSelected = selectedFromNav ?? selectedDevIds;
    const effectiveColorMap: Record<number, string> =
      colorMapFromNav ? { ...currentColorMap, ...colorMapFromNav } : currentColorMap;

    const colorForNow = (devId: number) =>
      effectiveColorMap[devId] ??
      initialDevIdToColorMap[devId] ??
      allColors[devId % allColors.length];

    if (mapRef.current) {
      const initialMap = L.map(mapRef.current);
      const storedZoom = localStorage.getItem('mapZoom');
      const storedCenterStr = localStorage.getItem('mapCenter');
      const storedCenter = storedCenterStr ? JSON.parse(storedCenterStr) : null;

      if (storedZoom && storedCenter) {
        initialMap.setView(storedCenter, parseInt(storedZoom));
      } else {
        initialMap.setView([34.3852, 132.4552], 15); //delault
      }

      initialMap.on('zoomend', () => {
        const currentZoom = initialMap.getZoom();
        localStorage.setItem('mapZoom', currentZoom.toString());
      });

      initialMap.on('moveend', () => {
        const currentCenter = initialMap.getCenter();
        calculateVisibleTiles(initialMap, client_id, u_id)
        localStorage.setItem('mapCenter', JSON.stringify([currentCenter.lat, currentCenter.lng]));
      });

      setMap(initialMap);

      //-----------prepare baselayers--------------
      const mapLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: "Map data © <a href='https://openstreetmap.org'>OpenStreetMap</a> contributors"
      });

      const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: "Map data © <a href='https://openstreetmap.org'>OpenStreetMap</a> contributors"
      });

      const baseLayer = (layer === '地図') ? mapLayer : satelliteLayer;
      baseLayer.addTo(initialMap)
      L.control.layers({ '地図': mapLayer, '衛星写真': satelliteLayer }).addTo(initialMap);
      initialMap.on('baselayerchange', (event) => {
        setLayer(event.name);
      });

      const markers: L.Marker[] = [];
      if (pins.length > 0 ){
        pins.forEach((pin) => {
          const selected = effectiveSelected.includes(pin.devId);
          const colorToUse = colorForNow(pin.devId);

          const marker = L.marker(pin.position, {
            icon: createIcon(colorToUse),
            opacity: selected ? 1 : 0,
            interactive: true,
            // when creating marksers, interactive always set true (means popup active),
            // but whether the popup is actually displayed will be based on CSS pointer-events
          }).addTo(initialMap);
        
          if (selected) {
            marker.bindPopup(
              `デバイス：${pin.name} <br /> 日付：${pin.dateTime} <br /> 緯度：${pin.position.lat} <br /> 経度：${pin.position.lng} <br /> 電池残量：${pin.adc} <br /> RSSI：${pin.rssi}`
            );
          }

          // if unselected, popup will not display by CSS pointer-events:none
          marker.on('add', () => {
            const el = (marker as any).getElement?.();
            if (el && !selected) el.style.pointerEvents = 'none'; 
          });
        
          setMarkerRefs(prevRefs => {
            const updatedRefs = new globalThis.Map(prevRefs);
            const arr = updatedRefs.get(pin.devId) || [];
            arr.push(marker);
            updatedRefs.set(pin.devId, arr);
            return updatedRefs;
            });
        });
      }

      setIsLoading(false);

      return () => {
        initialMap.remove();
        markers.forEach(marker => marker.remove());
      };
    }
  }, [pins]);

  useEffect(() => {
    localStorage.setItem('selectedLayer', layer);
    // console.log(layer);
  }, [layer]);

  useEffect(() => {
    const fetchLatestPositions = async () => {
      try {
        if (allDevicesData.length === 0) return;

        const devIds = allDevicesData.map((device: DeviceData) => device.dev_id);
        const latestDataResults: Data[] = await getLastestDataBatch(devIds);

        const { dataAvailability, newPins } = buildLatestDataResult(allDevicesData, latestDataResults);

        setDeviceDataAvailability(dataAvailability);
        setPins(newPins);

        const visibleTilesCount = localStorage.getItem('visibleTilesCount');
        const usage: UsageRecord = {
          use_date: new Date().toISOString(),
          client_id: client_id,
          u_id: u_id,
          event_type: 0,
          tile_count: visibleTilesCount ? parseInt(visibleTilesCount) : 0
        };
        addUsageRecord(usage);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const intervalId = setInterval(fetchLatestPositions, 60000);

    return () => clearInterval(intervalId);
  }, [pins, selectedDevIds]);

  //----------------reset setting-------------------
  const reset = () => {
    fetchData();
  };

  //--------------ViewAllPins-------------------
  const handleViewAllClick = () => {
    const selectedPins = pins.filter(pin => selectedDevIds.includes(pin.devId));
    if (!map || selectedPins.length === 0) return;

    const bounds = L.latLngBounds(selectedPins.map(pin => pin.position));

    // check Navbar's heigh anf give safe space for avoiding navbar covering pin
    const navEl = document.querySelector('.navbar') as HTMLElement | null;
    const topPad = (navEl?.offsetHeight ?? 56) + 20; // Navbar +12 px 

    const rightPad = 20;
    const leftPad = 20;
    const bottomPad = 20;

    map.fitBounds(bounds, {
      paddingTopLeft: [leftPad, topPad],
      paddingBottomRight: [rightPad, bottomPad],
      maxZoom: 18
    });
  };

  //--------------HandleVisibility-------------------
  const handleCheckboxChange = (devId: number, devColor: string, initialColor: string) => {
    const isSelected = selectedDevIds.includes(devId);
    const markers = markerRefs.get(devId);
    const pin = pins.find(p => p.devId === devId); // for popup content

    if (isSelected) {
      const next = selectedDevIds.filter(id => id !== devId);
      setSelectedDevIds(next);
      setCurrentColorMap(prev => {
        const n = { ...prev };
        delete n[devId];
        return n;
      });

      // undisplay pin(by setting opacity) & popup(by CSS pointer-events:none)
      markers?.forEach(marker => {
        marker.setOpacity(0);
        marker.closePopup();
        const el = (marker as any).getElement?.();
        if (el) el.style.pointerEvents = 'none';
      });
      return;
    }

    if (selectedDevIds.length >= 10) {
      alert('デバイスは最大で10個しか選択できません。');
      return;
    }

    // default color be first, if the color already is used，get new color
    const preferred = initialDevIdToColorMap[devId];
    const used = new Set(Object.values(currentColorMap));
    const colorToUse = used.has(preferred)
    ? (allColors.find(c => !used.has(c)) || preferred)
    : preferred;
    
    setCurrentColorMap(prev => ({ ...prev, [devId]: colorToUse }));
    const nextSelected = [...selectedDevIds, devId];
    setSelectedDevIds(nextSelected);
    
    // display pin(by setting opacity) & popup (by CSS pointer-events:auto)
    if (markers) {
      const updatedIcon = createIcon(colorToUse);
      markers.forEach(marker => {
        marker.setIcon(updatedIcon);
        marker.setOpacity(1);
        if (pin) {
          marker.unbindPopup();
          marker.bindPopup(
            `デバイス：${pin.name} <br /> 日付：${pin.dateTime} <br /> 緯度：${pin.position.lat} <br /> 経度：${pin.position.lng} <br /> 電池残量：${pin.adc} <br /> RSSI：${pin.rssi}`
          );
        }
        const el = (marker as any).getElement?.();
        if (el) el.style.pointerEvents = 'auto';
      });
    }
  };

  return (
    <div className="App">
      <Navbar collapseOnSelect expand="lg" className="navbar">
        <Navbar.Brand href="/eltres/tracking">ElTRES</Navbar.Brand>
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Offcanvas placement="end" style={{ width: "200px" }}>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              ElTRES
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Nav className="justify-content-end flex-grow-1 pe-3 nav-tab">
              <Nav.Link href="/eltres/tracking" style={{ color: '#bbbcbd' }}>現在地</Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/eltres/movingHistory"
                state={{ selected: selectedDevIds, colorMap: currentColorMap }}
              >
                移動履歴
              </Nav.Link>
              {showAdminLinks && (
                <>
                  <Nav.Link href="/eltres/clients" hidden={isClientAdmin}>取引先設定</Nav.Link>
                  <Nav.Link href="/eltres/users">ユーザ設定</Nav.Link>
                  <Nav.Link href="/eltres/devices">端末設定</Nav.Link>
                  <Nav.Link href="/eltres/payloads" hidden={isClientAdmin}>ペイロード設定</Nav.Link>
                  <Nav.Link href="/eltres/usageAmount" hidden={isClientAdmin}>使用量</Nav.Link>
                </>
              )}
              <Nav.Link href="/eltres/download">ダウンロード</Nav.Link>
              <hr />
              <Nav.Link href="/eltres/changePWD">パスワード変更</Nav.Link>
              <Nav.Link onClick={() => uId ? Logout(uId) : moveTo('/eltres/login')}>ログアウト</Nav.Link>
            </Nav>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Navbar>

      <div className="map-container" ref={mapRef} style={{ position: 'relative', marginTop: '0px', width: '100%', height: '100vh' }}></div>

      <div className="pin-list">
        <button className="btn btn-viewAll" onClick={handleViewAllClick}>全面見る</button>
        <div className="custom-dropdown">
          <button className="btn btn-dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)} >デバイス</button>
          {isDropdownOpen && (
            <div className="custom-dropdown-content">
              <button className="btn btn-reset" onClick={reset}>リセット</button>
              <div className="device-list">
                {allDevicesData.length > 0 ? (
                  allDevicesData.map((device) => (
                    <div key={device.dev_id} className="device-list-item">
                      <input
                        type="checkbox"
                        id={`checkbox-${device.dev_id}`}
                        className="checkbox-item"
                        checked={selectedDevIds.includes(device.dev_id)}
                        onChange={() => handleCheckboxChange(device.dev_id, pins.find(pin => pin.devId === device.dev_id)?.color || 'default', pins.find(pin => pin.devId === device.dev_id)?.initialColor || '')}
                      />
                      {device.dev_name}
                      {!deviceDataAvailability[device.dev_id] && <span className="device-x">✕</span>}
                    </div >
                  ))
                ) : (
                  <div style={{ color: 'red' }}>ユーザーに紐づけてる装置の情報がありません。</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {isLoading && (
        <div className="loading-overlay" aria-busy="true" aria-live="polite">
          <div className="spinner" />
          <div className="loading-text">読み込み中...</div>
        </div>
      )}
    </div>
  );
}

export default Tracking;
